import { getAPI, postAPI } from "./apiMethods";
import { PROMPTS_PROCESS_ENDPOINT } from "./endpoints";
import api from "./api";
// Add endpoints to endpoints.js if needed

const PROMPTS_ENDPOINT = "/prompts";

export async function getPrompts(params = {}) {
  const responseData = await getAPI(PROMPTS_ENDPOINT, { params });

  // Case 1: The response is the paginated object { prompts: [], ... }
  if (responseData && responseData.prompts) {
    return responseData;
  }

  // Case 2: The response is a flat array []
  if (Array.isArray(responseData)) {
    return {
      prompts: responseData,
      totalPages: 1,
      currentPage: 1,
      totalCount: responseData.length,
    };
  }

  // Fallback for any other unexpected structure
  return { prompts: [], totalPages: 0, currentPage: 1, totalCount: 0 };
}

export async function submitPromptData(promptData) {
  return postAPI(PROMPTS_ENDPOINT, promptData);
}

export async function validateApiKey(apiKey, keyType) {
  try {
    // Send API key in custom headers for security instead of request body
    const headers = {};
    if (keyType === "gemini") {
      headers["x-gemini-key"] = apiKey;
    } else if (keyType === "assembly") {
      headers["x-assembly-key"] = apiKey;
    }

    const response = await api.post(
      `${PROMPTS_ENDPOINT}/validate-key`,
      {
        keyType,
      },
      { headers }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
}

export const processPrompts = async (
  file,
  geminiKey,
  assemblyAIKey,
  promptsData,
  selectedFolder = null,
  folderName = null
) => {
  try {
    // Required: Gemini API key
    if (!geminiKey) {
      throw new Error("Gemini API key is required");
    }

    // Convert promptsData to the required format
    const prompts = [];

    // Chapter prompt is required
    prompts.push({
      type: "chapter",
      prompt: promptsData.chapterPrompt || undefined,
    });

    // Add optional prompts if enabled
    if (promptsData.assessmentPrompt?.enabled) {
      prompts.push({
        type: "assessment",
        prompt: promptsData.assessmentPrompt.text || undefined,
      });
    }

    if (promptsData.summaryPrompt?.enabled) {
      prompts.push({
        type: "summary",
        prompt: promptsData.summaryPrompt.text || undefined,
      });
    }

    if (promptsData.tagsPrompt?.enabled) {
      prompts.push({
        type: "tags",
        prompt: promptsData.tagsPrompt.text || undefined,
      });
    }

    if (promptsData.trailerPrompt?.enabled) {
      prompts.push({
        type: "trailer",
        prompt: promptsData.trailerPrompt.text || undefined,
      });
    }

    // Create secure headers for API keys
    const secureHeaders = {
      "x-gemini-key": geminiKey,
    };

    // Add AssemblyAI key to headers if provided
    if (assemblyAIKey) {
      secureHeaders["x-assembly-key"] = assemblyAIKey;
    }

    let response;

    // If there's no file, send JSON payload
    if (!file) {
      const jsonPayload = {
        prompts: prompts,
        folderName: folderName || undefined,
        selectedFolder: selectedFolder || undefined,
      };

      response = await api.post(PROMPTS_PROCESS_ENDPOINT, jsonPayload, {
        headers: {
          "Content-Type": "application/json",
          ...secureHeaders,
        },
      });
    }
    // If there's a file, use FormData
    else {
      const formData = new FormData();
      formData.append("file", file);
      // Remove API keys from FormData - they're now in headers

      formData.append("prompts", JSON.stringify(prompts));
      if (folderName) formData.append("folderName", folderName);
      if (selectedFolder) formData.append("selectedFolder", selectedFolder);

      response = await api.post(PROMPTS_PROCESS_ENDPOINT, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...secureHeaders,
        },
      });
    }

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to process prompts");
    }

    return response.data;
  } catch (error) {
    console.error("Process prompts error:", error);
    throw error.response?.data || error;
  }
};
